import * as React from 'react'

import { InteractiveFieldStyled } from './styles/InteractiveFieldStyled'

export interface IInteractiveFieldProps {
  onClick: (
    fieldId: string,
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void
  fieldId: string
  children: React.ReactNode
  'aria-label'?: string
  disabled?: boolean
  tabIndex?: number
}

export const InteractiveField = ({
  onClick,
  fieldId,
  children,
  'aria-label': ariaLabel,
  disabled,
  tabIndex = -1, // Default to -1 to prevent focus. This encourages keyboard interaction to only happen at the card level rather than each field.
  ...props
}: IInteractiveFieldProps) => {
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // Note: we're explicitly allowing the event to bubble up if the field is disabled. If the field is not disabled, then we handle it here and
      // prevent it from bubbling up.
      if (!disabled) {
        e.stopPropagation() // Prevent card click
        onClick(fieldId, e)
      }
    },
    [disabled, onClick, fieldId]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault()
      e.stopPropagation()
      onClick(fieldId, e)
    }
  }

  return (
    <InteractiveFieldStyled
      role='button'
      tabIndex={disabled ? -1 : tabIndex}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      disabled={disabled} // This is for styling purposes only; Need aria-disabled for accessibility.
      {...props}
    >
      {children}
    </InteractiveFieldStyled>
  )
}
